import { motion } from "motion/react";
import { Eye, Settings, TrendingUp, Target, BookOpen, LogOut, ArrowRight } from "lucide-react";
import { ExperienceButton } from "./ExperienceButton";

interface WelcomeScreenProps {
  onStart: () => void;
}

const criteria = [
  {
    icon: Eye,
    title: "SEE",
    subtitle: "Transparency & Visibility",
    question: "Can you see how the AI works?",
    description: "Evaluates whether the vendor exposes the prompts, models, and architecture being used — or operates as a black box.",
    color: "#4DA6D4",
    colorDark: "#3B8DB8",
  },
  {
    icon: Settings,
    title: "CHANGE",
    subtitle: "Customizability & Control",
    question: "Can you modify AI behavior?",
    description: "Assesses your ability to adjust prompts, select models, and configure the system to fit your organization's needs.",
    color: "#9B4FD4",
    colorDark: "#7E3AB5",
  },
  {
    icon: TrendingUp,
    title: "ADAPT",
    subtitle: "Vendor Agility & Innovation",
    question: "Does the vendor keep pace?",
    description: "Measures how quickly the vendor adopts frontier AI capabilities and whether you're locked to a lagging update cycle.",
    color: "#5BBFB5",
    colorDark: "#45A199",
  },
  {
    icon: Target,
    title: "USE",
    subtitle: "Genuine Utility & Value",
    question: "Is the tool actually useful?",
    description: "Determines whether the tool provides real, measurable value — or merely the impression of usefulness.",
    color: "#C47B9F",
    colorDark: "#A86485",
  },
  {
    icon: BookOpen,
    title: "LEARN",
    subtitle: "Transferable Knowledge",
    question: "Do you build portable AI skills?",
    description: "Evaluates whether using the tool builds generalizable AI capabilities across your organization, or only teaches vendor-specific workflows.",
    color: "#5C6BC0",
    colorDark: "#4A57A3",
  },
  {
    icon: LogOut,
    title: "EXIT",
    subtitle: "Portability & Migration",
    question: "Can you leave cleanly?",
    description: "Assesses the difficulty of transitioning away — including data portability, format lock-in, and switching costs.",
    color: "#C4785A",
    colorDark: "#A86245",
  },
];

export function WelcomeScreen({ onStart }: WelcomeScreenProps) {
  return (
    <div className="min-h-screen relative overflow-hidden bg-[#063F6B] text-white">
      {/* Subtle radial glow for depth */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] bg-[#124C77]/40 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#EAA059]/[0.06] rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 sm:py-28">
        {/* Header section */}
        <motion.div className="text-center mb-20">
          {/* Thin accent line */}
          <motion.div
            className="w-12 h-[2px] bg-[#EAA059] mx-auto mb-8"
            initial={{ width: 0 }}
            animate={{ width: 48 }}
            transition={{ delay: 0.1, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          />

          <motion.h1
            className="text-4xl sm:text-5xl lg:text-7xl font-semibold text-white mb-6 leading-[1.1] tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            AI Vendor Evaluation
          </motion.h1>

          <motion.p
            className="text-lg sm:text-xl text-slate-300/90 max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            An interactive experience that walks you through six dimensions
            of AI vendor assessment.
          </motion.p>
        </motion.div>

        {/* Six dimension cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 mb-20">
          {criteria.map((criterion, index) => {
            const Icon = criterion.icon;
            return (
              <motion.div
                key={criterion.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.08, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                whileHover={{ y: -4, transition: { duration: 0.25 } }}
                className="group p-6 lg:p-7 rounded-2xl bg-white/[0.05] border border-white/[0.08] hover:bg-white/[0.08] transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md transition-shadow duration-300"
                    style={{ background: `linear-gradient(135deg, ${criterion.color}, ${criterion.colorDark})` }}
                  >
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white tracking-tight">{criterion.title}</h3>
                    <p className="text-sm font-medium" style={{ color: '#EAA059' }}>{criterion.subtitle}</p>
                  </div>
                </div>
                <p className="text-white/90 font-medium text-[15px] mb-1.5">
                  {criterion.question}
                </p>
                <p className="text-slate-400 leading-relaxed text-sm">
                  {criterion.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Paramount question + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-center"
        >
          <div className="inline-block rounded-2xl p-7 sm:p-8 border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm mb-12 max-w-3xl">
            <p className="text-base sm:text-lg text-slate-300/90 leading-relaxed">
              <span className="text-[#EAA059] font-semibold">The paramount question:</span>{" "}
              Does this tool support or delay your organization's capacity to work effectively with AI?
            </p>
          </div>

          <div>
            <ExperienceButton
              onClick={onStart}
              className="px-10 py-4 bg-gradient-to-r from-[#EAA059] to-[#d4893e] hover:from-[#d4914d] hover:to-[#c07830] text-white text-lg font-medium rounded-full transition-all duration-300 shadow-lg shadow-[#EAA059]/20 hover:shadow-xl hover:shadow-[#EAA059]/30"
            >
              Begin Evaluation
              <ArrowRight className="ml-3 w-5 h-5" />
            </ExperienceButton>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
